import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import api from "../lib/api";
import { toast } from "sonner";
import { User, Mail, Key, ShieldCheck, MapPin, Calendar, Gamepad2, Megaphone, ArrowRight } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [country, setCountry] = useState("");
  const [age, setAge] = useState("");
  const [steamId, setSteamId] = useState("");
  const [referral, setReferral] = useState("");
  
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== repeatPassword) {
        setError("Parolele nu se potrivesc!");
        return;
    }
    setLoading(true);
    setError("");
    try {
      const payload = {
          email,
          username,
          password,
          country,
          age: parseInt(age) || null,
          steam_id: steamId,
          referral_source: referral
      };
      await register(payload);
      toast.success("Cont creat cu succes! Bine ai venit!");
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSSO = async (provider) => {
    try {
      const redirectUri = window.location.origin + "/app/auth/callback";
      const { data } = await api.get(`/auth/${provider}/login?redirect_uri=${encodeURIComponent(redirectUri)}`);
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-[90vh] flex items-center justify-center p-4">
      <div className="w-full max-w-2xl relative">
        {/* Glow effect behind card */}
        <div className="absolute inset-0 bg-[var(--tf-primary)]/20 blur-[100px] rounded-full pointer-events-none" />
        
        <div className="tf-surface tf-clip-card border border-white/5 relative z-10 overflow-hidden shadow-2xl backdrop-blur-xl">
          {/* Header Area */}
          <div className="p-8 pb-6 border-b border-white/5 bg-black/20 text-center relative overflow-hidden">
             <div className="inline-flex items-center justify-center w-12 h-12 rounded bg-[var(--tf-primary)]/10 mb-4 border border-[var(--tf-primary)]/20">
               <ShieldCheck className="w-6 h-6 text-[var(--tf-primary)]" />
             </div>
             <h1 className="tf-heading text-3xl font-bold tracking-tight text-white mb-2 uppercase">Înregistrare</h1>
             <p className="text-sm text-zinc-400">Completează profilul tău de operator pentru a te alătura rețelei INDUNGI.</p>
          </div>

          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded flex items-start gap-2">
                   <div className="mt-0.5">⚠</div>
                   <div className="flex-1">{error}</div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                  {/* Left Column */}
                  <div className="space-y-4">
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider block ml-1">Nume Utilizator *</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <User className="h-4 w-4 text-zinc-500" />
                          </div>
                          <input
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                            minLength={3}
                            className="w-full bg-black/40 border border-white/10 rounded py-2.5 pl-10 pr-4 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[var(--tf-primary)]/50 focus:ring-1 focus:ring-[var(--tf-primary)]/50 transition-all text-sm"
                            placeholder="ex: Player1"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider block ml-1">Adresă Email *</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Mail className="h-4 w-4 text-zinc-500" />
                          </div>
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="w-full bg-black/40 border border-white/10 rounded py-2.5 pl-10 pr-4 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[var(--tf-primary)]/50 focus:ring-1 focus:ring-[var(--tf-primary)]/50 transition-all text-sm"
                            placeholder="operator@indungi.ro"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider block ml-1">Parolă *</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Key className="h-4 w-4 text-zinc-500" />
                          </div>
                          <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={6}
                            className="w-full bg-black/40 border border-white/10 rounded py-2.5 pl-10 pr-4 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[var(--tf-primary)]/50 focus:ring-1 focus:ring-[var(--tf-primary)]/50 transition-all text-sm"
                            placeholder="••••••••••••"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider block ml-1">Repetă Parola *</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Key className="h-4 w-4 text-zinc-500" />
                          </div>
                          <input
                            type="password"
                            value={repeatPassword}
                            onChange={(e) => setRepeatPassword(e.target.value)}
                            required
                            className={`w-full bg-black/40 border ${repeatPassword && password !== repeatPassword ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500/50' : 'border-white/10 focus:border-[var(--tf-primary)]/50 focus:ring-[var(--tf-primary)]/50'} rounded py-2.5 pl-10 pr-4 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 transition-all text-sm`}
                            placeholder="••••••••••••"
                          />
                        </div>
                      </div>
                  </div>
                  
                  {/* Right Column */}
                  <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1.5">
                            <label className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider block ml-1">Țara *</label>
                            <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <MapPin className="h-4 w-4 text-zinc-500" />
                            </div>
                            <input
                                type="text"
                                value={country}
                                onChange={(e) => setCountry(e.target.value)}
                                required
                                className="w-full bg-black/40 border border-white/10 rounded py-2.5 pl-10 pr-4 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[var(--tf-primary)]/50 focus:ring-1 focus:ring-[var(--tf-primary)]/50 transition-all text-sm"
                                placeholder="ex: România"
                            />
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider block ml-1">Vârstă *</label>
                            <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Calendar className="h-4 w-4 text-zinc-500" />
                            </div>
                            <input
                                type="number"
                                min="10"
                                max="100"
                                value={age}
                                onChange={(e) => setAge(e.target.value)}
                                required
                                className="w-full bg-black/40 border border-white/10 rounded py-2.5 pl-10 pr-4 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[var(--tf-primary)]/50 focus:ring-1 focus:ring-[var(--tf-primary)]/50 transition-all text-sm"
                                placeholder="ex: 20"
                            />
                            </div>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider block ml-1">Cum ai aflat de noi? *</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Megaphone className="h-4 w-4 text-zinc-500" />
                          </div>
                          <select
                            value={referral}
                            onChange={(e) => setReferral(e.target.value)}
                            required
                            className="w-full bg-black/40 border border-white/10 rounded py-2.5 pl-10 pr-4 text-white focus:outline-none focus:border-[var(--tf-primary)]/50 focus:ring-1 focus:ring-[var(--tf-primary)]/50 transition-all text-sm appearance-none"
                          >
                            <option value="" disabled className="bg-[#111] text-zinc-500">Alege o opțiune...</option>
                            <option value="Google" className="bg-[#111]">Google</option>
                            <option value="Prieteni" className="bg-[#111]">De la un prieten</option>
                            <option value="ServerCS" className="bg-[#111]">De pe un server CS</option>
                            <option value="SocialMedia" className="bg-[#111]">Rețele sociale</option>
                            <option value="Altceva" className="bg-[#111]">Altceva</option>
                          </select>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider block ml-1">Cont Steam (Opțional)</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Gamepad2 className="h-4 w-4 text-zinc-500" />
                          </div>
                          <input
                            type="text"
                            value={steamId}
                            onChange={(e) => setSteamId(e.target.value)}
                            className="w-full bg-black/40 border border-white/10 rounded py-2.5 pl-10 pr-4 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[var(--tf-primary)]/50 focus:ring-1 focus:ring-[var(--tf-primary)]/50 transition-all text-sm"
                            placeholder="Link profil sau SteamID"
                          />
                        </div>
                        <p className="text-[10px] text-zinc-500 ml-1">Ne ajută la statistici și afișare profil.</p>
                      </div>

                  </div>
              </div>

              <div className="pt-2">
                <button
                    type="submit"
                    disabled={loading || (password !== repeatPassword && password.length > 0)}
                    className="w-full bg-[var(--tf-primary)] hover:bg-[var(--tf-primary)]/90 text-black font-bold uppercase tracking-wider py-3 px-4 rounded transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed group"
                >
                    {loading ? "Se creează contul..." : "Creează Contul"}
                    {!loading && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
                </button>
              </div>
            </form>

            <div className="mt-8 pt-6 border-t border-white/5">
              <p className="text-[10px] font-bold tracking-widest text-zinc-500 uppercase text-center mb-4">
                Sau conectează-te cu
              </p>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => handleSSO("steam")}
                  className="bg-[#171a21] hover:bg-[#2a475e] text-white text-xs font-bold py-2 px-2 rounded border border-white/5 transition-colors flex items-center justify-center gap-2"
                >
                  <Gamepad2 className="w-4 h-4 opacity-70" />
                  Steam
                </button>
                <button
                  type="button"
                  onClick={() => handleSSO("discord")}
                  className="bg-[#5865F2]/20 hover:bg-[#5865F2] text-[#5865F2] hover:text-white border border-[#5865F2]/30 hover:border-[#5865F2] text-xs font-bold py-2 px-2 rounded transition-all flex items-center justify-center gap-2"
                >
                  Discord
                </button>
                <button
                  type="button"
                  onClick={() => handleSSO("google")}
                  className="bg-white/5 hover:bg-white/10 text-zinc-300 hover:text-white text-xs font-bold py-2 px-2 rounded border border-white/10 transition-colors"
                >
                  Google
                </button>
              </div>
            </div>

            <p className="text-sm text-zinc-500 text-center mt-8">
              Ai deja cont?{" "}
              <Link to="/login" className="text-[var(--tf-primary)] font-semibold hover:underline">
                Autentifică-te aici
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
